"use client";

import type React from "react";
import { useState } from "react";
import {
  FormControl,
  FormLabel,
  Text,
  Button,
  VStack,
  HStack,
  IconButton,
  Box,
  useDisclosure,
  Tooltip,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { Plus } from "lucide-react";
import { useAppTheme } from "../../../ThemeContext.tsx";
import { PronunciationModal } from "./PronunciationModal.tsx";

export interface PronunciationEntry {
  word: string;
  alphabet: "ipa" | "cmu";
  phoneme: string;
}

interface PronunciationDictionaryProps {
  value: PronunciationEntry[] | null | undefined;
  onChange: (value: PronunciationEntry[] | null) => Promise<boolean> | void;
}

export const PronunciationDictionary: React.FC<
  PronunciationDictionaryProps
> = ({ value = null, onChange }) => {
  const [editingEntry, setEditingEntry] = useState<PronunciationEntry | null>(
    null
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // --------------------------
  //  Styling - same as snippet
  // --------------------------
  const cardBg = isDark ? "gray.900" : "white";
  const itemBg = isDark ? "gray.700" : "gray.50";
  const textColor = isDark ? "white" : "gray.800";
  const subtextColor = isDark ? "gray.400" : "gray.600";
  const borderColor = isDark ? "gray.600" : "gray.300";

  const handleAdd = async (entry: PronunciationEntry) => {
    setIsUpdating(true);
    try {
      const newEntries = [...(value || []), entry];
      await onChange(newEntries);
      onClose();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEdit = async (updatedEntry: PronunciationEntry) => {
    if (!editingEntry) return;
    setIsUpdating(true);
    try {
      const newEntries =
        value?.map((entry) =>
          entry.word === editingEntry.word ? updatedEntry : entry
        ) || [];
      await onChange(newEntries);
      setEditingEntry(null);
      onClose();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (wordToDelete: string) => {
    setIsUpdating(true);
    try {
      const newEntries =
        value?.filter((entry) => entry.word !== wordToDelete) || [];
      await onChange(newEntries.length > 0 ? newEntries : null);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenEdit = (entry: PronunciationEntry) => {
    setEditingEntry(entry);
    onOpen();
  };

  const handleModalClose = () => {
    setEditingEntry(null);
    onClose();
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
        Pronunciation
      </FormLabel>
      <Text fontSize="xs" color={subtextColor} mb={2}>
        Guide the model to pronounce a word, name, or phrase in a specific way.
        {/* (
        <Link
          href="https://docs.retellai.com/build/add-pronounciation"
          color={subtextColor}
          isExternal
        >
          Learn more <ExternalLinkIcon mx="2px" />
        </Link>
        ) */}
      </Text>

      <VStack spacing={2} align="stretch">
        {value?.map((entry) => (
          <HStack
            key={entry.word}
            spacing={2}
            p={1.5}
            bg={itemBg}
            borderRadius="md"
            justify="space-between"
            minHeight="32px"
          >
            <Box>
              <Text fontSize="sm" color={textColor} fontWeight="medium">
                {entry.word}
              </Text>
              <Text fontSize="xs" color={subtextColor}>
                {entry.alphabet.toUpperCase()}: {entry.phoneme}
              </Text>
            </Box>
            <HStack>
              <Tooltip label="Edit pronunciation">
                <IconButton
                  aria-label="Edit pronunciation"
                  icon={<EditIcon boxSize={3} />}
                  size="xs"
                  variant="ghost"
                  isDisabled={isUpdating}
                  onClick={() => handleOpenEdit(entry)}
                  color={isDark ? "gray.300" : "gray.700"}
                  _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
                />
              </Tooltip>
              <Tooltip label="Delete pronunciation">
                <IconButton
                  aria-label="Delete pronunciation"
                  icon={<DeleteIcon boxSize={3} />}
                  size="xs"
                  variant="ghost"
                  isDisabled={isUpdating}
                  onClick={() => handleDelete(entry.word)}
                  color={isDark ? "gray.300" : "gray.700"}
                  _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
                />
              </Tooltip>
            </HStack>
          </HStack>
        ))}

        <Button
          leftIcon={<Plus size={14} />}
          size="xs"
          variant="outline"
          isDisabled={isUpdating}
          bg="transparent"
          color={textColor}
          borderColor={borderColor}
          _hover={{ bg: cardBg }}
          onClick={onOpen}
        >
          Add
        </Button>
      </VStack>

      <PronunciationModal
        isOpen={isOpen}
        onClose={handleModalClose}
        onSave={editingEntry ? handleEdit : handleAdd}
        initialData={editingEntry}
      />
    </FormControl>
  );
};
